/**
 * Configuration initiale globale et Polyfills
 */
document.body.classList.add('is-page-transition');

if ('scrollRestoration' in history) {
    history.scrollRestoration = 'manual';
}

import './modules/@tc';
import 'classlist-polyfill';
import 'object-fit-polyfill';
import barba from '@barba/core';

// Sélecteurs globaux
const overlay = document.querySelector('.transition-overlay');
const logo = document.querySelector('.logo');
const scrollContainer = document.querySelector('.dialog-off-canvas-main-canvas');

/**
 * -----------------------------------------------------------------------------
 * GESTION DU PARALLAX (Optimisé et sécurisé)
 * -----------------------------------------------------------------------------
 */
let parallaxRaf = null;
let parallaxRunning = false;
const parallaxItems = new Set();
let observer = null; // On le déclare vide au départ

function createObserver() {
  // On ne recrée pas l'observer s'il existe déjà
  if (observer) return;

  // On cherche le scroller au moment précis de l'initialisation
  const scrollerForObserver = document.querySelector('.dialog-off-canvas-main-canvas');

  observer = new IntersectionObserver((entries) => {
    for (const entry of entries) {
      if (entry.isIntersecting) {
        parallaxItems.add(entry.target);
      } else {
        parallaxItems.delete(entry.target);
      }
    }
  }, {
    root: scrollerForObserver || null, // Cible enfin la bonne div de scroll
    threshold: 0,
    rootMargin: "300px 0px"
  });
}

function stopParallax() {
  parallaxRunning = false;
  if (parallaxRaf) {
    cancelAnimationFrame(parallaxRaf);
    parallaxRaf = null;
  }
  if (observer) {
    parallaxItems.forEach(el => observer.unobserve(el));
  }
  parallaxItems.clear();
}

function initParallax(container = document) {
    stopParallax();
    createObserver(); // On s'assure que l'observer est créé avec le bon container racine
  
    const items = container.querySelectorAll('.parallax');
    if (!items.length) return;
  
    items.forEach(el => {
      observer.observe(el);
      el._scale = el._scale || 1;
      el._offsetTop = undefined; // Force le recalcul de position absolue
    });
  
    startParallax();
}

function startParallax() {
  if (parallaxRunning) return;
  parallaxRunning = true;

  let current = 0;
  const ease = 0.06; // Ton inertie globale

  const getScroll = () => {
    const scroller = getScrollContainer();
    return scroller === window ? window.scrollY : scroller.scrollTop;
  };

  const animate = () => {
    if (!parallaxRunning) return;

    const target = getScroll();
    current += (target - current) * ease;

    const isMobile = window.innerWidth < 992;

    parallaxItems.forEach(item => {
      const isThumbnail = item.classList.contains('thumbnail-projet') || item.closest('.thumbnail-projet');

      // 1. TRANSLATE (Uniquement sur Desktop et hors vignettes)
      if (!isMobile && !isThumbnail) {
        const speed = parseFloat(item.dataset.speed || 0.1);
        const y = current * speed;
        item.style.transform = `translate3d(0, ${y}px, 0)`;
      } else {
        if (item.style.transform && item.style.transform !== 'none') {
          item.style.transform = 'none';
        }
      }

      // 2. SCALE (Calculé dynamiquement, fonctionne PARTOUT)
      const img = item.querySelector('img');
      if (!img || !item.closest('.img-container')) return;

      if (item._offsetTop === undefined) {
        const rect = item.getBoundingClientRect();
        const scroller = getScrollContainer();
        const initialScroll = scroller === window ? window.scrollY : scroller.scrollTop;
        item._offsetTop = rect.top + initialScroll;
      }

      const vh = window.innerHeight;
      const elementTopRelativeToViewport = item._offsetTop - current;

      const progress = Math.max(0, Math.min(1, 1 - elementTopRelativeToViewport / vh));
      
      const startScale = parseFloat(item.dataset.scaleStart || 1.15);
      const targetScale = startScale - (startScale - 1) * progress;

      item._scale += (targetScale - item._scale) * 0.08; 
      img.style.transform = `scale(${item._scale})`;
    });

    parallaxRaf = requestAnimationFrame(animate);
  };

  parallaxRaf = requestAnimationFrame(animate);
}

/**
 * -----------------------------------------------------------------------------
 * OUTILS & UTILS DOM
 * -----------------------------------------------------------------------------
 */
function fixLazyImages() {
    document.querySelectorAll('.bloc-realisations .thumbnail-projet img').forEach(img => img.loading = 'eager');
}

function getScrollContainer() {
  return document.querySelector('.dialog-off-canvas-main-canvas') || window;
}

const scroller = getScrollContainer();

/**
 * -----------------------------------------------------------------------------
 * COMPOSANT : HEADER (Scroll intelligent)
 * -----------------------------------------------------------------------------
 */
const header = document.getElementById('header');
let lastScroll = 0;
let scrollUpDistance = 0;
const showHeaderAfter = 50; 
let isScrolling = false;

scroller.addEventListener('scroll', () => {
  if (!isScrolling) {
    isScrolling = true;
    window.requestAnimationFrame(() => {
      const currentScroll = scroller === window ? window.scrollY : scroller.scrollTop;

      if (currentScroll > lastScroll && currentScroll > 120) {
        scrollUpDistance = 0;
        header?.classList.add('scrolled');
        header?.classList.remove('not-top');
      } else if (currentScroll < lastScroll) {
        scrollUpDistance += lastScroll - currentScroll;

        if (scrollUpDistance >= showHeaderAfter) {
          header?.classList.remove('scrolled');
          if (currentScroll > 0) {
            header?.classList.add('not-top');
          }
        }
      }

      if (currentScroll === 0) {
        header?.classList.remove('not-top');
        header?.classList.remove('scrolled');
      }

      lastScroll = currentScroll;
      isScrolling = false; 
    });
  }
}, { passive: true });

/**
 * -----------------------------------------------------------------------------
 * COMPOSANT : POPUP CONTACT & MENU
 * -----------------------------------------------------------------------------
 */
function popupOpen() {
    const popupBtn = document.getElementById('popupBtn');
    const popupOverlay = document.querySelector('.popup-overlay');

    if (popupBtn) {
        popupBtn.addEventListener('click', () => document.body.classList.toggle('popup-open'));
    }
    if (popupOverlay) {
        popupOverlay.addEventListener('click', () => document.body.classList.remove('popup-open'));
    }
    document.addEventListener('keydown', (event) => {
        if (event.key === 'Escape' && document.body.classList.contains('popup-open')) {
            document.body.classList.remove('popup-open');
        }
    });
}

/**
 * -----------------------------------------------------------------------------
 * COMPOSANT : ACCORDÉONS FAQ
 * -----------------------------------------------------------------------------
 */
function initAccordions(container = document) {
    const accordions = container.querySelectorAll('[role="tablist"]');
    if (!accordions.length) return;

    function getScrollOffset() {
        const w = window.innerWidth;
        if (w < 576) return 240;
        if (w < 768) return 210;
        return 220;
    }

    function scrollToElement(el) {
        const container = getScrollContainer();
        const rect = el.getBoundingClientRect();
        const offset = getScrollOffset();

        if (container === window) {
            window.scrollTo({ top: window.scrollY + rect.top - offset, behavior: 'smooth' });
            return;
        }

        const cRect = container.getBoundingClientRect();
        container.scrollTo({
            top: container.scrollTop + (rect.top - cRect.top) - offset,
            behavior: 'smooth'
        });
    }

    accordions.forEach(accordion => {
        const toggles = accordion.querySelectorAll('.card-header a');
        const panels = accordion.querySelectorAll('.collapse');

        panels.forEach(panel => {
            if (panel.dataset.ready) return;
            panel.style.overflow = 'hidden';
            panel.style.maxHeight = '0px';
            panel.style.transition = 'max-height 300ms ease';
            panel.dataset.ready = '1';
            panel.dataset.open = '0';
        });

        function closePanel(panel, toggle) {
            panel.dataset.open = '0';
            panel.style.maxHeight = panel.scrollHeight + 'px';
            
            requestAnimationFrame(() => {
                panel.style.maxHeight = '0px';
            });

            panel.classList.remove('open');
            panel.setAttribute('aria-hidden', 'true');

            if (toggle) {
                toggle.setAttribute('aria-expanded', 'false');
                toggle.classList.add('collapsed');
                toggle.closest('.card-header')?.classList.remove('open');
            }
        }

        function openPanel(panel, toggle) {
            panel.dataset.open = '1';
            panel.classList.add('open');
            panel.setAttribute('aria-hidden', 'false');

            const height = panel.scrollHeight;
            panel.style.maxHeight = height + 'px';

            if (toggle) {
                toggle.setAttribute('aria-expanded', 'true');
                toggle.classList.remove('collapsed');
                toggle.closest('.card-header')?.classList.add('open');
            }

            const handleTransitionEnd = () => {
                if (panel.dataset.open === '1') {
                    panel.style.maxHeight = 'none';
                }
                panel.removeEventListener('transitionend', handleTransitionEnd);
            };
            panel.addEventListener('transitionend', handleTransitionEnd);

            requestAnimationFrame(() => {
                const header = toggle?.closest('.card-header');
                if (header) scrollToElement(header);
            });
        }

        toggles.forEach(toggle => {
            if (toggle.dataset.init) return;
            toggle.dataset.init = '1';
            toggle.setAttribute('role', 'button');

            if (!toggle.hasAttribute('aria-expanded')) {
                toggle.setAttribute('aria-expanded', 'false');
            }

            toggle.addEventListener('click', e => {
                e.preventDefault();
                const target = toggle.getAttribute('href') || ('#' + toggle.getAttribute('aria-controls'));
                const panel = accordion.querySelector(target);
                if (!panel) return;

                if (panel.dataset.open === '1') {
                    closePanel(panel, toggle);
                } else {
                    panels.forEach(p => {
                        if (p !== panel) {
                            const t = accordion.querySelector(`[href="#${p.id}"]`);
                            closePanel(p, t);
                        }
                    });
                    openPanel(panel, toggle);
                }
            });
        });
    });
}

/**
 * -----------------------------------------------------------------------------
 * ANCRE SCROLL TO CONTENT
 * -----------------------------------------------------------------------------
 */
function initScrollTo() {
  document.addEventListener('click', (e) => {
    const btn = e.target.closest('#toContent');
    if (!btn) return;

    e.preventDefault();
    const target = document.getElementById('scrollContent');
    const currentScroller = getScrollContainer();
    if (!target) return;

    const offset = 100;
    const position = target.getBoundingClientRect().top + (currentScroller === window ? window.scrollY : currentScroller.scrollTop) - offset;

    currentScroller.scrollTo({ top: position, behavior: 'smooth' });
  });
}

/**
 * -----------------------------------------------------------------------------
 * ANIMATIONS D'APPARITION (Intersection Observer)
 * -----------------------------------------------------------------------------
 */
function initInView(container = document) {
    const elements = container.querySelectorAll('.js-inview');
    if (!elements.length) return;
  
    const observer = new IntersectionObserver((entries, obs) => {
      entries.forEach(entry => {
        if (entry.isIntersecting) {
          entry.target.classList.add('in-view');
          obs.unobserve(entry.target);
        }
      });
    }, {
      threshold: 0,
      rootMargin: '0px 0px -200px 0px'
    });
  
    elements.forEach(el => observer.observe(el));
}

/**
 * -----------------------------------------------------------------------------
 * ÉTATS DE NAVIGATION BARBA.JS
 * -----------------------------------------------------------------------------
 */
function updateLogoState() {
    const logoLink = document.querySelector('#branding'); 
    if (!logoLink) return;
    const currentPath = window.location.pathname;
    const logoHref = new URL(logoLink.href, window.location.origin).pathname;
    
    if (logoHref === currentPath) {
      logoLink.classList.add('inactive'); 
    } else {
      logoLink.classList.remove('inactive'); 
    }
}

function relaunchScripts(newContainer = document) {
    initAccordions(newContainer);
    const path = window.location.pathname.replace(/\/$/, '');

    const updateLinks = (selector) => {
        document.querySelectorAll(selector).forEach(link => {
            link.classList.remove('is-active');
            const href = link.getAttribute('href').replace(/\/$/, '');
            if (href === path) link.classList.add('is-active');
            
            const li = link.closest('li');
            if (href === '/realisations' && path.startsWith('/realisations') && path !== '/realisations') {
                li?.classList.add('nav-item-active-trail');
            } else {
                li?.classList.remove('nav-item-active-trail');
            }
        });
    };

    updateLinks('header .nav-link');
    updateLinks('footer a');
}

/**
 * -----------------------------------------------------------------------------
 * FONCTIONS DE VERROUILLAGE PHYSIQUE DU SCROLL NAVIGATEUR
 * -----------------------------------------------------------------------------
 */
function killScrollEvent(e) { e.preventDefault(); }
function lockBrowserScroll() {
  window.addEventListener('wheel', killScrollEvent, { passive: false });
  window.addEventListener('touchmove', killScrollEvent, { passive: false });
}
function unlockBrowserScroll() {
  window.removeEventListener('wheel', killScrollEvent);
  window.removeEventListener('touchmove', killScrollEvent);
}

const nextFrame = () => new Promise(resolve => requestAnimationFrame(resolve));
const wait = (ms) => new Promise(resolve => setTimeout(resolve, ms));

/**
 * Séquence de transition Overlay + Logo initiale
 */
async function playOverlaySequence(firstLoad = false, callback) {
    if (!overlay || !logo) {
        if (callback) callback();
        return;
    }
  
    lockBrowserScroll(); 
    updateLogoState();
  
    overlay.style.display = 'flex';
    overlay.style.transition = 'none';
    logo.style.transition = 'none';
  
    logo.style.opacity = '0';
    overlay.style.opacity = firstLoad ? '1' : '0';
    overlay.style.pointerEvents = 'auto'; 
  
    await nextFrame();
  
    logo.style.transition = 'opacity 0.4s ease';
    logo.style.opacity = '1';
  
    if (!firstLoad) {
        overlay.style.transition = 'opacity 0.5s ease';
        overlay.style.opacity = '1';
    }

    await wait(1200); 
  
    logo.style.transition = 'opacity 0.4s ease';
    logo.style.opacity = '0';
  
    overlay.style.transition = 'opacity 0.5s ease';
    overlay.style.opacity = '0';
    overlay.style.pointerEvents = 'none'; 
  
    await wait(500); 
  
    overlay.style.display = 'none';
    unlockBrowserScroll(); 
  
    document.body.classList.remove('first-visit');
    if (callback) callback();
}

/**
 * -----------------------------------------------------------------------------
 * INITIALISATION : BARBA.JS ROUTING & TRANSITIONS
 * -----------------------------------------------------------------------------
 */
barba.init({
  transitions: [
    {
      name: 'overlay-transition',
      leave({current}) {
        stopParallax(); 
          
        lockBrowserScroll(); 
        document.body.classList.add('is-page-transition');

        current.container.querySelectorAll('.in-view').forEach(el => el.classList.remove('in-view')); 

        overlay.style.display = 'flex';
        overlay.style.opacity = '0';
        overlay.style.pointerEvents = 'auto'; 
        overlay.style.transition = 'opacity 0.5s ease';
        logo.style.opacity = '0';
        logo.style.transition = 'none';

        return new Promise(resolve => {
          requestAnimationFrame(() => {
            overlay.style.opacity = '1';
            setTimeout(() => {
              document.body.classList.remove('popup-open');
              resolve();
            }, 500); 
          });
        });
      },
      enter(data) {
        stopParallax();
        relaunchScripts(data.next.container);
        updateLogoState();
        document.body.classList.remove('lock-loaded');
      
        window.scrollTo(0, 0);
        if (scrollContainer) {
          scrollContainer.scrollTo({ top: 0, behavior: 'auto' });
        }
      
        logo.style.transition = 'opacity 0.4s ease';
        logo.style.opacity = '1';
      
        setTimeout(() => {
          logo.style.transition = 'opacity 0.4s ease';
          logo.style.opacity = '0';
      
          overlay.style.transition = 'opacity 0.5s ease';
          overlay.style.opacity = '0';
          overlay.style.pointerEvents = 'none';
      
          setTimeout(() => {
            overlay.style.display = 'none';
            document.body.classList.remove('is-page-transition');
            unlockBrowserScroll();
      
            requestAnimationFrame(() => {
              initInView(data.next.container);
              initParallax(data.next.container);
            });
          }, 500);
        }, 900);
      
        document.body.classList.add('lock-loaded');
      },
      afterEnter() {
        fixLazyImages();
      }
    }
  ],
  prevent: ({ el }) => el.href && (el.href.includes('/admin') || el.href.includes('/user'))
});

/**
 * -----------------------------------------------------------------------------
 * CHARGEMENT INITIAL DE LA PAGE (Événements natifs)
 * -----------------------------------------------------------------------------
 */
window.addEventListener('load', () => {
    document.body.classList.add('lock-loaded');
    popupOpen();
    initScrollTo(); 

    playOverlaySequence(true, () => {
        relaunchScripts();
        document.body.classList.remove('is-page-transition');
    
        setTimeout(() => {
          initInView();
          initParallax();
        }, 200);
    });
});
  
document.addEventListener('DOMContentLoaded', fixLazyImages);